export function generateDescription(formData) {
  const {
    itemCondition, // fallback if `condition` is not set
    condition,
    artist,
    fullTitle,
    releaseYear,
    label,
    type,
    totalQty,
    series,
    color,
    notesForDescription,
    keySellingPoints,
    conditionSleeve, // Add sleeve condition for checking
  } = formData;

  const actualCondition = condition || itemCondition;

  const artistStr = Array.isArray(artist) ? artist.join(", ") : artist || "";
  const dashTitle = [artistStr, fullTitle].filter(Boolean).join(" - ");

  const isBoxSet =
    typeof type === "string" && type.toLowerCase().includes("box");

  const boxSetPrefix =
    isBoxSet && totalQty
      ? `${totalQty} LP vinyl Box Set`
      : isBoxSet
      ? "vinyl Box Set"
      : "vinyl LP";

  // ── Line 1 ───────────────────────────────────────────────────────────────────
  const line1 =
    actualCondition?.toLowerCase() === "new"
      ? `This is a brand new, sealed ${boxSetPrefix} of: ${dashTitle}.`
      : `This is the ${boxSetPrefix} of: ${dashTitle}.`;

  // ── Line 2 ───────────────────────────────────────────────────────────────────
  const line2 = (() => {
    if (releaseYear && label) {
      return `This killer ${releaseYear} ${
        isBoxSet ? "Box Set" : "release"
      } is from the fine folks at ${label}.`;
    }
    if (releaseYear) {
      return `This killer ${releaseYear} ${
        isBoxSet ? "Box Set" : "release"
      } is from a great label.`;
    }
    if (label) {
      return `This killer early pressing ${
        isBoxSet ? "Box Set" : "release"
      } is from the fine folks at ${label}.`;
    }
    return "";
  })();

  // ── Line 2.5 (series note, if any) ───────────────────────────────────────────
  const line2_5 = (() => {
    if (Array.isArray(series) && series.length > 0) {
      const name = series[0]?.name?.trim();
      if (name) {
        return `This is part of the fantastic ${name} series.`;
      }
    }
    return "";
  })();

  // ── Notes bullet points from Discogs ─────────────────────────────────────────
  const notesParagraph = (() => {
    if (!notesForDescription || !Array.isArray(notesForDescription) || notesForDescription.length === 0) {
      return "";
    }

    const bulletList = notesForDescription
      .map(note => `- ${note}`)
      .join('\n');

    return bulletList;
  })();

  // ── Color Variant paragraph ──────────────────────────────────────────────────
  const colorVariantParagraph = (() => {
    if (!color) return "";
    
    const normalizedColor = color.toLowerCase().trim();
    
    // Skip common "standard" colors that don't need highlighting
    const skipColors = [
      'black', 
      'standard', 
      'standard black',
      'regular'
    ];
    
    if (skipColors.includes(normalizedColor)) return "";

    // Skip if the notes already mention this color
    if (notesForDescription && Array.isArray(notesForDescription)) {
      const notesText = notesForDescription.join(' ').toLowerCase();
      if (notesText.includes(normalizedColor)) return "";
    }

    // Handle special cases and clean up color description
    let cleanColor = color.trim();
    
    // Handle transparent/clear variants
    if (normalizedColor.includes('clear') || normalizedColor.includes('transparent')) {
      cleanColor = cleanColor.replace(/\b(opaque|translucent)\b/gi, '').trim();
    }
    
    // Capitalize first letter of each word for better presentation
    cleanColor = cleanColor.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    
    return `Color Variant: ${cleanColor} Vinyl.`;
  })();

  // ── Line 3 (used-note) ───────────────────────────────────────────────────────
  const usedNote = (() => {
    if (actualCondition?.toLowerCase() === "new") {
      return "";
    }

    // Skip personal claim for older/undated records — not credible
    const yearNum = parseInt(releaseYear, 10);
    const isOlderRecord = !releaseYear || !yearNum || yearNum < 2005;

    // Check if sleeve condition is below NM
    const belowNMConditions = ['Excellent (EX)', 'Very Good Plus (VG+)', 'Very Good (VG)', 'Good Plus (G+)', 'Good (G)', 'Fair (F)', 'Worn (W)'];
    const isSleeveBelowNM = conditionSleeve && belowNMConditions.includes(conditionSleeve);

    if (isOlderRecord) {
      return isSleeveBelowNM ? "See pics!!" : "";
    }

    let baseNote = "I bought this brand new and only listened to it once or twice.";

    if (isSleeveBelowNM) {
      baseNote += " See pics!!";
    } else {
      baseNote += " No issues!";
    }

    return baseNote;
  })();

  // ── Line 4 (shipping) ────────────────────────────────────────────────────────
  const shippingNote =
    "Fast shipping from Music City, USA!! I only use proper wide mailers for maximum protection in transit. I never use those terrible square mailers. See my feedback!!";

  // ── Key Selling Points paragraph ─────────────────────────────────────────────
  const keySellingPointsParagraph = (() => {
    if (!keySellingPoints || !Array.isArray(keySellingPoints) || keySellingPoints.length === 0) {
      return "";
    }

    // Filter out selling points already covered in the notes section
    // Normalize hyphens/spaces so "Foil Stamped" matches "Foil-Stamped"
    const normalize = (s) => s.toLowerCase().replace(/[-–—]/g, ' ').replace(/\s+/g, ' ');
    const notesNorm = (notesForDescription || []).map(normalize);
    const dedupedPoints = keySellingPoints.filter(point => {
      const pointNorm = normalize(point);
      return !notesNorm.some(note =>
        note.includes(pointNorm) || pointNorm.includes(note)
      );
    });

    if (dedupedPoints.length === 0) return "";

    // Check if selling points contain color info (to avoid duplication)
    const hasColorInSellingPoints = dedupedPoints.some(point =>
      point.toLowerCase().includes('vinyl') &&
      (point.toLowerCase().includes('color') ||
       point.toLowerCase().includes('ninja') ||
       point.toLowerCase().includes('magic') ||
       point.toLowerCase().includes('swirl') ||
       point.toLowerCase().includes('splatter'))
    );

    // If we have color variant info AND selling points don't contain color, combine them
    if (colorVariantParagraph && !hasColorInSellingPoints) {
      return `${colorVariantParagraph} ${dedupedPoints.join(", ")}.`;
    }

    // Otherwise, selling points get their own paragraph (skipping basic color variant)
    const sellingPointsText = dedupedPoints.join(". ") + ".";
    // Capitalize first letter of the paragraph
    return sellingPointsText.charAt(0).toUpperCase() + sellingPointsText.slice(1);
  })();

  // ── Assemble paragraphs ─────────────────────────────────────────────────────
  const firstParagraph = [line1, line2, line2_5].filter(Boolean).join(" ");

  return [
    firstParagraph,
    notesParagraph,
    keySellingPointsParagraph, // Now handles color variant logic internally
    usedNote,
    shippingNote,
  ]
    .filter(Boolean)
    .join("\n\n");
}
